/* -------------------------------------------------------------------------- *
 * Toast notifications with optional "Undo" action.
 *
 * Gmail-style destructive-action pattern: the caller performs the visual
 * removal immediately and registers a toast with an `onCommit` (run when the
 * toast expires without undo) and `onUndo` (run if the user clicks Undo).
 * The real, irreversible work (e.g. the DynamoDB delete) happens in
 * `onCommit`, so undo never needs to reverse a completed write.
 *
 * Visible window: at most MAX_VISIBLE toasts are shown at once — the newest
 * ones. Extra toasts stay alive (their timers keep running and Undo still
 * works) but are hidden from view until a visible slot frees up. Timers are
 * time-based: a toast commits DURATION_MS after it was created regardless of
 * whether it is currently visible (matches Gmail's behaviour).
 * -------------------------------------------------------------------------- */

const DEFAULT_DURATION_MS = 5000;
const MAX_VISIBLE = 3;

let container = null;

// Active toasts in creation order (oldest first, newest last). The newest
// MAX_VISIBLE are the ones shown.
const toasts = [];

let flushWired = false;

/**
 * Commit every pending toast immediately. Called when the page is being hidden
 * or unloaded so a deferred destructive action (e.g. a DynamoDB delete that
 * normally fires when the toast expires) is not lost if the user navigates away
 * before the timer fires.
 *
 * The commit handlers issue `keepalive` fetches (see core/aws.js), so requests
 * dispatched here survive the page unload.
 */
export function flushToasts() {
  // Iterate over a copy: commit() mutates `toasts` via teardown().
  for (const t of [...toasts]) t.commit?.();
}

/**
 * Wire (once) the page-lifecycle listeners that flush pending toasts. We use
 * `pagehide` and `visibilitychange→hidden` rather than `beforeunload`/`unload`
 * because those are the events that fire reliably across navigations, tab
 * close, and mobile/bfcache.
 */
function ensureFlushWired() {
  if (flushWired) return;
  flushWired = true;
  window.addEventListener('pagehide', flushToasts);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushToasts();
  });
}

/** Lazily create (once) the fixed container that holds stacked toasts. */
function ensureContainer() {
  if (container) return container;
  container = document.createElement('div');
  container.className = 'toast-container';
  container.setAttribute('aria-live', 'polite');
  document.body.appendChild(container);
  return container;
}

/**
 * Recompute which toasts are visible: only the newest MAX_VISIBLE. Hidden
 * toasts keep their DOM node (so timers/undo handlers survive) but get the
 * `toast-hidden` class which removes them from view.
 */
function reflow() {
  const firstVisible = Math.max(0, toasts.length - MAX_VISIBLE);
  toasts.forEach((t, i) => {
    t.el.classList.toggle('toast-hidden', i < firstVisible);
  });
}

/** Remove a toast from the registry + DOM, then reflow the visible window. */
function teardown(t) {
  if (t.timer) clearTimeout(t.timer);
  const idx = toasts.indexOf(t);
  if (idx !== -1) toasts.splice(idx, 1);
  t.el.classList.add('toast-leaving');
  setTimeout(() => t.el.remove(), 200);
  reflow();
}

/**
 * Show a toast.
 *
 * @param {object}   opts
 * @param {string}   opts.message     text to display
 * @param {Function} [opts.onCommit]  run when the toast expires without undo
 * @param {Function} [opts.onUndo]    run if the user clicks Undo
 * @param {number}   [opts.duration]  ms before auto-commit (default 5000)
 * @returns {{ dismiss: Function }}   handle to programmatically commit early
 */
export function showToast({ message, onCommit, onUndo, duration = DEFAULT_DURATION_MS }) {
  const root = ensureContainer();
  ensureFlushWired();

  const el = document.createElement('div');
  el.className = 'toast';

  const text = document.createElement('span');
  text.className = 'toast-message';
  text.textContent = message;
  el.appendChild(text);

  const t = { el, timer: null, settled: false, commit: null };

  const commit = () => {
    if (t.settled) return;
    t.settled = true;
    teardown(t);
    if (onCommit) onCommit();
  };
  // Exposed so flushToasts() can force-commit pending toasts on page unload.
  t.commit = commit;

  const undo = () => {
    if (t.settled) return;
    t.settled = true;
    teardown(t);
    if (onUndo) onUndo();
  };

  if (onUndo) {
    const undoBtn = document.createElement('button');
    undoBtn.type = 'button';
    undoBtn.className = 'toast-undo';
    undoBtn.textContent = 'Undo';
    undoBtn.addEventListener('click', undo);
    el.appendChild(undoBtn);
  }

  root.appendChild(el);
  toasts.push(t);
  reflow();
  // Time-based: runs DURATION_MS after creation whether visible or not.
  t.timer = setTimeout(commit, duration);

  return { dismiss: commit };
}
